import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useSafmr } from "@/hooks/use-safmr";
import { useSection8 } from "@/hooks/use-section8";
import { usePipeline } from "@/hooks/use-pipeline";
import { MockProperty } from "@/data/mockProperties";
import { DollarSign, TrendingUp, TrendingDown, CheckCircle, AlertTriangle } from "lucide-react";
import { DomainTermInfo } from "@/components/ui/domain-term-info";

export function RentReasonablenessTab() {
  const { cases } = useSection8();
  const { savedProperties } = usePipeline();

  // Live FMR from safmr_rates (DB), keyed by each case's property ZIP.
  const zips = useMemo(
    () =>
      cases
        .map((c) => {
          const sp = savedProperties.find((s) => s.id === c.pipeline_property_id);
          return (sp?.property_data as unknown as MockProperty | undefined)?.zip;
        })
        .filter((z): z is string => Boolean(z)),
    [cases, savedProperties],
  );
  const { getFmr } = useSafmr(zips);

  const analysis = useMemo(() => {
    return cases.map((c) => {
      const sp = savedProperties.find((s) => s.id === c.pipeline_property_id);
      const pd = sp?.property_data as unknown as MockProperty | undefined;
      const askingRent = c.asking_rent ? Number(c.asking_rent) : pd?.estimatedRent || 0;
      const paymentStandard = c.payment_standard ? Number(c.payment_standard) : 0;
      const utilityAllowance = c.utility_allowance ? Number(c.utility_allowance) : 0;
      // Live SAFMR by ZIP/city/bedrooms; fall back to the baked value if the
      // ZIP isn't in the table yet.
      const fmr = getFmr(pd?.zip, pd?.city ?? "", pd?.bedrooms ?? 0) ?? pd?.section8Fmr ?? 0;
      const maxGrossRent = paymentStandard || fmr;
      const hapPayment = Math.max(0, maxGrossRent - utilityAllowance);
      const tenantPortion = Math.max(0, askingRent - hapPayment);
      const isAboveFMR = askingRent > fmr;
      const isAbovePaymentStandard = paymentStandard > 0 && askingRent > paymentStandard;
      const fmrDiff = fmr > 0 ? ((askingRent - fmr) / fmr) * 100 : 0;

      return {
        caseId: c.id,
        address: pd?.address || "Unknown",
        beds: pd?.bedrooms || 0,
        baths: pd?.bathrooms || 0,
        askingRent,
        fmr,
        paymentStandard,
        utilityAllowance,
        hapPayment,
        tenantPortion,
        isAboveFMR,
        isAbovePaymentStandard,
        fmrDiff,
        status: c.status,
      };
    });
  }, [cases, savedProperties, getFmr]);

  if (cases.length === 0) {
    return (
      <Card className="shadow-soft">
        <CardContent className="py-12 text-center">
          <DollarSign className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-muted-foreground">Create a Section 8 case to see rent analysis</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-foreground">Rent Reasonableness Analysis</h3>
        <p className="text-sm text-muted-foreground flex flex-wrap items-center gap-x-1 gap-y-1">
          <span>Compare your asking rent against FMR and</span>
          <span className="inline-flex items-center gap-0.5">
            PHA
            <DomainTermInfo termId="PHA" />
          </span>
          <span>payment standards</span>
        </p>
      </div>

      {analysis.map((a) => {
        const approvalLikely = !a.isAbovePaymentStandard && !a.isAboveFMR;
        return (
          <Card key={a.caseId} className="shadow-soft">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium">{a.address}</CardTitle>
                <Badge variant="outline" className={approvalLikely ? "bg-emerald-500/10 text-emerald-500" : "bg-amber-500/10 text-amber-500"}>
                  {approvalLikely ? <><CheckCircle className="h-3 w-3 mr-1" />Likely Approved</> : <><AlertTriangle className="h-3 w-3 mr-1" />Review Needed</>}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">{a.beds} bed / {a.baths} bath</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground">Your Asking</p>
                  <p className="text-lg font-bold text-foreground">${a.askingRent.toLocaleString()}</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground flex items-center justify-center gap-0.5">
                    FMR
                    <DomainTermInfo termId="FMR" />
                  </p>
                  <p className="text-lg font-bold text-foreground">${a.fmr.toLocaleString()}</p>
                  {a.fmr > 0 && (
                    <p className={`text-xs flex items-center justify-center gap-0.5 ${a.isAboveFMR ? "text-amber-500" : "text-emerald-500"}`}>
                      {a.isAboveFMR ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      {a.fmrDiff > 0 ? "+" : ""}{a.fmrDiff.toFixed(1)}%
                    </p>
                  )}
                </div>
                <div className="text-center p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground flex items-center justify-center gap-0.5">
                    Est. HAP Payment
                    <DomainTermInfo termId="HAP" />
                  </p>
                  <p className="text-lg font-bold text-emerald-500">${a.hapPayment.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground flex items-center justify-center gap-0.5">
                    After UA: ${a.utilityAllowance}
                    <DomainTermInfo termId="utility_allowance" />
                  </p>
                </div>
                <div className="text-center p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground">Tenant Pays</p>
                  <p className="text-lg font-bold text-foreground">${a.tenantPortion.toLocaleString()}</p>
                </div>
              </div>
              {a.isAbovePaymentStandard && (
                <div className="mt-3 p-2 rounded-md bg-amber-500/10 text-amber-600 text-sm">
                  ⚠️ Asking rent exceeds PHA payment standard (${a.paymentStandard.toLocaleString()}). Consider reducing to ${a.paymentStandard.toLocaleString()} for faster approval.
                </div>
              )}
              {!a.isAboveFMR && a.fmr > 0 && (
                <div className="mt-3 p-2 rounded-md bg-emerald-500/10 text-emerald-600 text-sm">
                  ✓ Asking rent is at or below FMR — strong position for rent reasonableness approval.
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
